import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import logo from "../../../Assets/logo.png";
import {
    AppBar,
    Badge,
    Button,
    FormHelperText,
    Grid,
    InputAdornment,
    InputLabel,
    Modal,
    TextField,
    useTheme
} from "@mui/material";
import Box from "@mui/material/Box";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import * as React from "react";
import {
    ACard,
    AMultiSelectOption,
    AMultiSelectOptionId,
    FloatMenuButton,
    FloatMenuItem,
} from "@atiautomacao/ati-ui-library";
import {faCalendarDays} from "@fortawesome/free-solid-svg-icons";
import {DarkModeOutlined, LightModeOutlined, Logout, Menu, Visibility, VisibilityOff} from "@mui/icons-material";
import Avatar from "@mui/material/Avatar";
import {toggleMainMenu, toggleSubMenu} from "../../../Config/Reducers/LayoutReducer";
import Breadcrumb from "./menu/Breadcrumb";
import {useAppDispatch, useAppSelector} from "../../../Config/Hooks";
import {QueryClient, QueryClientProvider} from "react-query";
import {useEffect, useMemo, useState} from "react";
import {useLocation} from "react-router-dom";
import {hasPermission} from "../../Auth/AuthenticationUtil";
import {AUTHORITIES, HEADER_LOGO_URL, POWER_STATION_IDS_FILTER_STORAGE_KEY} from "../../../Config/Constants";
import {useSnackbar} from "notistack";
import Tooltip from "@mui/material/Tooltip";
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import axios from "axios";
import { TicketUpdateModal } from '../../../Modules/Dashboard/Ticket/TicketModal';
import { RootState } from "../../../Config/Store";
import { getTotalizers } from "../AlarmTotalizers/AlarmTotalizersReducer";
import {getAlarmTotalizersRefreshPolicy} from "../AlarmTotalizers/AlarmTotalizersRefreshPolicy";
import useInterval from "../../Hooks/useInterval";
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import {
    isCommunicationEquipmentsPage,
    isCommunicationTeleobjectsPage,
    isAuditCommandsPage,
    isAuditAlarmsPage,
    isDashboardPage,
    isSettingsUsersPage,
    isDashboardOverviewPerformancePage,
    isDashboardPowerPlantInvertersPage,
    isDashboardDailyReportPage,
    isDashboardDailyViewPage,
} from "../../../Utils/RouteUtils";
import { usePersistedSelection } from "../../Hooks/usePersistedSelection";

const style = {
    logo: {
        height: 38,
        width: 'auto',
        maxWidth: 220,
        objectFit: 'contain' as const
    },
};

const headerActionIconButtonSx = {
    width: 30,
    height: 30,
    borderRadius: '20px',
    '&:hover': {
        backgroundColor: '#c1c1c1'
    },
} as const;

interface SGDAppBarProps {
    handleThemeMode: () => void;
}


function SGDAppBar({handleThemeModeChange}: any){
    const theme = useTheme();
    const location = useLocation();
    const storedTheme = localStorage.getItem("theme") ?? "light";
    const [mode, setMode] = useState<"light" | "dark">(storedTheme as "light" | "dark");
    const { enqueueSnackbar } = useSnackbar();
    
    const isAuthenticated = useAppSelector((state: any) => state.authentication.isAuthenticated);
    const dispatch = useAppDispatch();
    const entitiesDashboard = useAppSelector((state: RootState) => state.dashboardNavigationMenu.entities);
    const openMainMenu = useAppSelector((state: any) => state.layout.openMainMenu);
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const account = useAppSelector((state) => state.authentication.account);
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    // SGD-3621: logo do header — por cliente (Configuration, pós-login/tenant) › por ambiente (env var) › padrão.
    const headerLogoUrl = userConfig.headerLogoUrl || HEADER_LOGO_URL;
    const { totalizers, isLoading } = useAppSelector((state: RootState) => state.alarmTotalizers);
    const alarmTotalizersRefreshPolicy = getAlarmTotalizersRefreshPolicy({
        pathname: location.pathname,
        isLoading,
        alarmUpdateInterval: userConfig.alarmUpdateInterval,
        alarmSoundCountRefreshIntervalByms: userConfig.alarmSoundCountRefreshIntervalByms,
    });

    // **
    // Alarms sounds
    // **
    const [isAudioMuted, setAudioMuted] = useState(localStorage.getItem("audioMuted") === "true");
    const [ countTotalizerAlarmSound, setCountTotalizerAlarmSound ] = useState(0);
    const [ isVibrating, setIsVibrating ] = useState(false);
    
    const powerStationOptions = useMemo<AMultiSelectOption[]>(
        () => [...entitiesDashboard]
            .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
            .map(e => ({ id: e.id, label: e.name })),
        [entitiesDashboard]
    );

    const powerStationOptionIds = useMemo<AMultiSelectOptionId[]>(
        () => powerStationOptions.map(o => o.id),
        [powerStationOptions]
    );

    const {
        selectedIds: selectedPowerStationIds,
    } = usePersistedSelection<AMultiSelectOptionId>({
        storageKey: POWER_STATION_IDS_FILTER_STORAGE_KEY,
        allOptionIds: powerStationOptionIds,
    });

    useEffect(() => {         
        if (isAuthenticated && !isLoading) {            
            dispatch(getTotalizers(selectedPowerStationIds));
        }
    },[isAuthenticated, selectedPowerStationIds])

    useInterval(() => {
        if (!isLoading) {
            dispatch(getTotalizers(selectedPowerStationIds));
        }
    }, alarmTotalizersRefreshPolicy.delay, undefined, alarmTotalizersRefreshPolicy.restartKey);

    useEffect(() => {
        setCountTotalizerAlarmSound(totalizers.alarmsSound);
        if(countTotalizerAlarmSound < totalizers.alarmsSound){ 
            triggerVibration(userConfig.alarmSoundAnimationIntervalDurationByms);
            if (totalizers.alarmsSound && totalizers.alarmsSound > 0) {
                console.log('🔔 Totalizers mudou - Ativando vibração do badge!', totalizers.alarmsSound);
                setIsVibrating(true);
                // Desativa a vibração após 2 segundos
                const timer = setTimeout(() => {
                    console.log('🔕 Desativando vibração do badge!');
                    setIsVibrating(false);
                }, userConfig.alarmSoundAnimationIntervalDurationByms);
                
                return () => clearTimeout(timer);
            }
        }
    }, [totalizers.alarmsSound]);

    // Versão configurável da animação (opcional)
    const triggerVibration = (duration: number = userConfig.alarmSoundAnimationIntervalDurationByms) => {
        console.log(`🔔 Ativando animação por ${duration}ms!`);
        setIsVibrating(true);
        const timer = setTimeout(() => {
            console.log('🔕 Desativando animação!');
            setIsVibrating(false);
        }, duration);
        
        return () => clearTimeout(timer);
    };


    // Alternativa usando sx prop para animação inline
    const getBadgeAnimationSx = () => {
        if (!isVibrating) return {} as const;
        
        
        return {
            '@keyframes customTremor': {
                '0%' : {
                    transform: 'scale(1)'
                },
                '50%' : {
                    transform: 'scale(1.25)'
                },
                '100%' : {
                    transform: 'scale(1)'
                }
            },
            animation: 'customTremor 0.6s ease-in-out infinite',
        };
    };

    const handleAudioMuted = () => {
        setAudioMuted(!isAudioMuted);
    }

    useEffect(() => {
        localStorage.setItem("audioMuted", isAudioMuted.valueOf() ? "true" : "false");
    }, [isAudioMuted]);

    const [openPasswordModal, setOpenPasswordModal] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
    const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
    const [showPasswordRepeat, setShowPasswordRepeat] = useState<boolean>(false);

    const [oldPassword, setOldPassword] = useState<string>("");
    const [newPassword, setNewPassword] = useState<string>("");
    const [passwordRepeat, setPasswordRepeat] = useState<string>("");

    useEffect(() => {
        localStorage.setItem("theme", mode);
    }, [mode]);

    const handleClosePasswordModal = () => {
        setOpenPasswordModal(false)
        setOldPassword("")
        setNewPassword("")
        setPasswordRepeat("")
        setShowOldPassword(false)
        setShowPasswordRepeat(false)
        setShowNewPassword(false)
    }

    const [themeMode, setThemeMode] = useState('light');

    const menuToogle = () => {
        if(openMainMenu && !openSubMenu) {
            dispatch(toggleMainMenu());
        }else {
            dispatch(toggleMainMenu());
            dispatch(toggleSubMenu());
        }

    }

    const handleThemeMode = () => {
        if(themeMode === 'light') {
            setThemeMode('dark');
            handleThemeModeChange('dark');
        }else {
            setThemeMode('light');
            handleThemeModeChange('light');
        }
    }
    const handleChangePassword = () => {
        if(oldPassword === "" || newPassword === "" || passwordRepeat === "" ){
            enqueueSnackbar("Existem campos obrigatórios vazios!", {variant: "warning"})
            return;
        }
        if(newPassword !== passwordRepeat){
            enqueueSnackbar("A nova senha e a confirmação da senha devem ser iguais!", {variant: "warning"})
            return;
        }
        const hasLetter = /[A-Za-z]/.test(newPassword);
        const hasNumber = /[0-9]/.test(newPassword);
        if (!hasLetter || !hasNumber) {
            enqueueSnackbar("A senha precisa ter letras e números", {variant: "warning"})
            return;
        }
        axios.put('api/users/change-password',{userId: Number(account?.id), oldPassword: oldPassword, newPassword: newPassword}).then(
            _=> {
                enqueueSnackbar("Senha alterada com sucesso",{variant: 'success'})
                handleClosePasswordModal()
            }
        ).catch(
            error => {
                const status = error.response?.status ?? error.response?.data?.status;
                if(status === 400){
                    enqueueSnackbar("Senha atual esta errada",{variant: 'error'})
                }else{
                    enqueueSnackbar("Error ao salvar a nova senha",{variant: 'error'})
                }
            }
        )
    };


    /* Handle Ticket Modal */
    const [openTicketModal, setOpenTicketModal] = useState(false);
    const handleOpenTicketModal = () => setOpenTicketModal(true);
    const handleCloseTicketModal = () => setOpenTicketModal(!openTicketModal);

    const isAuthorizedToTicketSave = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, 
        AUTHORITIES.SAVE_TICKET
    ]);

    const isAuthorizedToTicketSearch = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_TICKET, AUTHORITIES.VIEW_TICKET
    ]);

    const isAuthorizedToTicketCategorySearch = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_TICKET_CATEGORY, AUTHORITIES.VIEW_TICKET_CATEGORY
    ]);

    const isAuthorizedToTicketSubCategorySearch = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_TICKET_SUBCATEGORY, AUTHORITIES.VIEW_TICKET_SUBCATEGORY
    ]);

    const isAuthorizedToView = () => {

        if(isAuthorizedToTicketSave && isAuthorizedToTicketSearch && isAuthorizedToTicketCategorySearch && isAuthorizedToTicketSubCategorySearch ){
            return true;
        }
        return false;
    
    }

    if(isAuthenticated) return (
        <AppBar position="fixed" sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1
        }}>
            <Toolbar style={{ paddingLeft: 7 }}>
                <IconButton
                    color={theme.palette.background.default === '#fff' ? 'inherit' : 'default'}
                    aria-label="open drawer"
                    edge="start"
                    onClick={menuToogle}
                    sx={{ ml: 2, mr: 2 }}
                >
                    <Menu />
                </IconButton>
                <IconButton edge="start" color="error" aria-label="Menu" style={{ marginLeft: 0 }}>
                    <img
                        alt="logo"
                        style={style.logo}
                        src={headerLogoUrl || logo}
                        onError={(e) => {
                            const img = e.currentTarget;
                            img.onerror = null; // evita loop caso o logo padrão também falhe
                            if (headerLogoUrl) {
                                console.error(`[SGDAppBarPage] Falha ao carregar o logo configurado (${headerLogoUrl}); usando o logo padrão.`);
                            }
                            img.src = logo;
                        }}
                    />
                </IconButton>
                {
                    // Breadcrumb no AppBar fica oculto nas telas que já renderizam o breadcrumb no conteúdo (App.tsx).
                    !(
                        isSettingsUsersPage()
                        || isCommunicationEquipmentsPage()
                        || isCommunicationTeleobjectsPage()
                        || isAuditCommandsPage()
                        || isAuditAlarmsPage()
                        || (isDashboardPage() && !isDashboardOverviewPerformancePage() && !isDashboardPowerPlantInvertersPage() && !isDashboardDailyReportPage() && !isDashboardDailyViewPage())
                    ) && <Breadcrumb />
                }
                <Box sx={{ flexGrow: 1}}></Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <IconButton name="ticket"
                        sx={headerActionIconButtonSx}
                        disabled={!isAuthorizedToView()}
                        onClick={handleOpenTicketModal}
                    >
                        <Tooltip title="Novo Ticket">
                            <LocalActivityIcon sx={{color: isAuthorizedToView() ? theme.palette.primary.main : '#b5b5b5'}} />
                        </Tooltip>
                    </IconButton>
                    <Tooltip title={themeMode === 'light' ? 'Modo Escuro' : 'Modo Claro'}>
                        <IconButton
                            onClick={handleThemeMode}
                            sx={headerActionIconButtonSx}
                        >
                            {
                                themeMode === 'light' ? <DarkModeOutlined sx={{color: theme.palette.primary.main}}/> : <LightModeOutlined sx={{color: theme.palette.primary.main}}/>
                            }
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={isAudioMuted ? '🔇 Desabilitar som' : '🔊 Habilitar som'}>
                        <Badge
                            color="error"
                            badgeContent={countTotalizerAlarmSound}
                            sx={{...getBadgeAnimationSx()}}
                        >
                            <IconButton
                                onClick={handleAudioMuted}
                                sx={headerActionIconButtonSx}
                            >
                                {
                                    isAudioMuted ?
                                    <VolumeUpIcon sx={{color: theme.palette.primary.main}}/> : <VolumeOffIcon sx={{color: theme.palette.primary.main}}/>
                                }
                            </IconButton>
                        </Badge>
                    </Tooltip>
                    {account ? (
                        <Box component="h5" sx={{ m: 0, whiteSpace: 'nowrap' }}>
                            {account.name}
                        </Box>
                    ) : null}
                    <FloatMenuButton
                        cardStyle={{transform: 'rotate(0deg)'}}
                        icon={<Avatar sx={{ width: 32, height: 32, border:'solid 2px #FFF', color: '#1656f8' }}>{account?.name ? account.name.charAt(0) : 'A'}</Avatar>}
                        tooltip={"Account Menu"}
                    >
                        <FloatMenuItem
                            icon={<FontAwesomeIcon icon={faCalendarDays} fontSize={20} />}
                            text="Trocar Senha"
                            disable={false}
                            onclick={() => setOpenPasswordModal(true)}
                        />
                        <FloatMenuItem
                            icon={<Logout fontSize="small" />}
                            text="Logout"
                            link="/logout"
                        />
                    </FloatMenuButton>
                </Box>
            </Toolbar>
                <Modal
                    open={openPasswordModal}
                    onClose={handleClosePasswordModal}
                    aria-labelledby="modal-title"
                    aria-describedby="modal-description"
                >
                    <Grid sx={{
                        position: 'absolute',
                        top: '40%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '80%', sm: '70%', md: '50%', lg: '30%' },
                        maxWidth: '600px',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        borderRadius: 4,
                    }}>
                        <ACard
                            title="Redefinir senha"
                            footerActions={
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                    <Button variant="contained" color="primary" onClick={handleClosePasswordModal}>
                                        Cancelar
                                    </Button>
                                    <Button variant="contained" color="primary" onClick={handleChangePassword}>
                                        Salvar
                                    </Button>
                                </Box>
                            }
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Box style={{marginBottom: 20, height: '50%'}}>
                                        <InputLabel id="old-password">Senha atual</InputLabel>
                                        <TextField
                                            error={oldPassword === ""}
                                            fullWidth
                                            id="old-password"
                                            type={showOldPassword ? "text" : "password"}
                                            variant="standard"
                                            placeholder={"Digite sua senha atual"}
                                            onChange={(value) => setOldPassword(value.target.value)}
                                            value={oldPassword}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() => setShowOldPassword(!showOldPassword)}
                                                        >
                                                            {showOldPassword ? <VisibilityOff/> : <Visibility/>}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box style={{marginBottom: 20, height: '50%'}}>
                                        <InputLabel id="new-password">Nova senha</InputLabel>
                                        <TextField
                                            error={newPassword === ""}
                                            fullWidth
                                            id="new-password"
                                            type={showNewPassword ? "text" : "password"}
                                            variant="standard"
                                            placeholder={"Digite sua nova senha"}
                                            onChange={(value) => setNewPassword(value.target.value)}
                                            value={newPassword}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                                        >
                                                            {showNewPassword ? <VisibilityOff/> : <Visibility/>}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box style={{marginBottom: 20, height: '50%'}}>
                                        <InputLabel id="password-repeat">Confirme a senha</InputLabel>
                                        <TextField
                                            error={passwordRepeat === ""}
                                            fullWidth
                                            id="password-repeat"
                                            type={showPasswordRepeat ? "text" : "password"}
                                            variant="standard"
                                            placeholder={"Confirme a senha digitada"}
                                            onChange={(value) => setPasswordRepeat(value.target.value)}
                                            value={passwordRepeat}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() => setShowPasswordRepeat(!showPasswordRepeat)}
                                                        >
                                                            {showPasswordRepeat ? <VisibilityOff/> : <Visibility/>}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                        {
                                            (passwordRepeat !== "" && newPassword !== "") && (passwordRepeat !== newPassword) &&
                                            <FormHelperText id="my-helper-text" style={{color: 'red'}}>As duas senhas devem ser iguais.</FormHelperText>
                                        }
                                    </Box>
                                </Grid>
                            </Grid>
                        </ACard>

                    </Grid>
                </Modal>

                {/* Ticket Modal */}
                <TicketUpdateModal ticketId={null} open={openTicketModal} onClose={handleCloseTicketModal} />
        </AppBar>
    );

    return null;
}


export default function SGDAppBarPage({handleThemeMode}: Readonly<SGDAppBarProps>){
    const queryClient = new QueryClient();
    return (
        <QueryClientProvider client={queryClient}>
            <SGDAppBar handleThemeModeChange={handleThemeMode}/>
        </QueryClientProvider>
    )
}
